import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect, useMemo } from 'react';
import Spinner from '../../shared/spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWallet, faArrowUp, faArrowDown, faFileInvoiceDollar,
    faFileUpload, faExclamationTriangle, faHandshake, 
    faChevronDown, faChevronUp, faTriangleExclamation, 
    faCircleExclamation, faBuilding
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../shared/customAlert';
import ComissaoService from '../../services/comissaoService';
import FaturaService from '../../services/faturaService';
import ArquivoService from '../../services/arquivoService';
import { format, addDays, isBefore } from 'date-fns';

// --- INTERFACES ---

// ATUALIZADA COM BASE NO SEU JSON
interface OrigemGenericaDTO {
    id: number;
    nome: string;      // O JSON retorna "nome"
    cnpj?: string;     // Opcional: útil para exibir no select
    ativo?: boolean;
}

interface ExtratoResumo {
    saldoAtual: number;
    totalCreditosPeriodo: number;
    totalDebitosPeriodo: number;
    lancamentos: Array<{
        id: number;
        dataMovimento: string;
        tipoLancamento: 'CREDITO' | 'DEBITO';
        origemLancamento: string;
        descricao: string;
        valorLancamento: number;
    }>;
}

interface FaturaClienteDTO {
    id: number;
    dataCriacao: string;
    valorLiquido: number;
    status: string;
    idNotaFiscal?: number;
    numeroNotaFiscal?: string;
    statusNotaFiscal?: string;
    motivoReprovacao?: string;
}

interface NegociacaoDTO {
    id: number;
    nomeNegociacao: string;
    valorTotal: number;
    saldoRestante: number;
    qtdParcelas: number;
    dataCriacao: string;
    status: 'PENDENTE' | 'EM_ANDAMENTO' | 'QUITADO' | 'CANCELADO';
    parcelas: ParcelaDTO[];
}

interface ParcelaDTO {
    id: number;
    numero: number;
    valor: number;
    dataVencimento: string;
    dataPagamento?: string;
    status: 'NAO_PAGO' | 'PAGO' | 'ATRASADO';
}

function FinanceiroParceiroComponent() {
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
    const [activeTab, setActiveTab] = useState<'EXTRATO' | 'FATURAS' | 'NEGOCIACOES'>('FATURAS');

    // --- ESTADOS DE SELEÇÃO ---
    const [promotoras, setPromotoras] = useState<OrigemGenericaDTO[]>([]);
    const [idParceiroSelecionado, setIdParceiroSelecionado] = useState<number>(0); 

    const [extrato, setExtrato] = useState<ExtratoResumo | null>(null);
    const [faturas, setFaturas] = useState<FaturaClienteDTO[]>([]);
    const [negociacoes, setNegociacoes] = useState<NegociacaoDTO[]>([]);

    const [idsSelecionados, setIdsSelecionados] = useState<number[]>([]);
    const [showModalUpload, setShowModalUpload] = useState(false);
    const [formUpload, setFormUpload] = useState({ numero: '', valor: '', arquivo: null as File | null });
    const [negociacaoExpandida, setNegociacaoExpandida] = useState<number | null>(null);

    const comissaoService = useMemo(() => new ComissaoService(), []);
    const faturaService = useMemo(() => new FaturaService(), []);
    const arquivoService = useMemo(() => new ArquivoService(), []);

    const isPrazoCritico = (dataCriacaoStr: string) => isBefore(addDays(new Date(dataCriacaoStr), 5), new Date());

    const faturasAtrasadas = useMemo(() => {
        return faturas.filter(f => isPrazoCritico(f.dataCriacao) && !f.idNotaFiscal && f.status !== 'PAGA');
    }, [faturas]);

    // 1. Carrega a lista de Promotoras
    useEffect(() => {
        const carregarPromotoras = async () => {
            try {
                const response = await arquivoService.listarOrigemGenerica('PROMOTORA');
                
                // O axios retorna { data: [...] }, então acessamos response.data
                const lista = response.data || []; 
                setPromotoras(lista);

                // Se a lista não estiver vazia e nenhum parceiro estiver selecionado, seleciona o primeiro
                if (lista.length > 0) {
                    // Tenta manter o ID se já existir, senão pega o primeiro
                    setIdParceiroSelecionado((prev) => prev !== 0 ? prev : lista[0].id);
                }
            } catch (error) {
                console.error("Erro ao carregar promotoras", error);
                setAlert({ message: 'Erro ao carregar lista de promotoras.', type: 'error' });
            }
        };
        carregarPromotoras();
    }, [arquivoService]);

    // 2. Carrega os dados financeiros quando o ID muda
    const carregarDados = async () => {
        if (!idParceiroSelecionado || idParceiroSelecionado === 0) return;

        try {
            setLoading(true);
            setExtrato(null);
            setFaturas([]);
            setNegociacoes([]);

            const [resExtrato, resFaturas] = await Promise.all([
                comissaoService.obterExtratoFinanceiro(idParceiroSelecionado),
                faturaService.listarFaturas({ termoBusca: idParceiroSelecionado.toString() })
            ]);

            setExtrato(resExtrato.data);
            const listaFaturas = Array.isArray(resFaturas.data) ? resFaturas.data : (resFaturas.data.content || []);
            setFaturas(listaFaturas.sort((a: any, b: any) => b.id - a.id));

            try {
                const resNegociacoes = await comissaoService.listarNegociacoesPorParceiro(idParceiroSelecionado);
                setNegociacoes(resNegociacoes.data);
            } catch (e) {
                setNegociacoes([]);
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
            setAlert({ message: 'Erro ao carregar dados financeiros.', type: 'error' });
        }
    };

    useEffect(() => {
        carregarDados();
        setIdsSelecionados([]); 
    }, [idParceiroSelecionado]);

    // --- MANIPULADORES DE EVENTOS ---
    const isElegivelSelecao = (fatura: FaturaClienteDTO) => {
        const sFatura = fatura.status?.toUpperCase();
        const sNota = fatura.statusNotaFiscal?.toUpperCase();
        const estaPaga = sFatura === 'PAGA' || sFatura === 'PAGO';
        const notaJaEnviada = sNota === 'EM_ANALISE' || sNota === 'APROVADA';
        return estaPaga && !notaJaEnviada;
    };

    const toggleSelecao = (id: number) => {
        idsSelecionados.includes(id)
            ? setIdsSelecionados(idsSelecionados.filter(i => i !== id))
            : setIdsSelecionados([...idsSelecionados, id]);
    };

    const toggleSelecionarTudo = () => {
        const elegiveis = faturas.filter(isElegivelSelecao).map(f => f.id);
        setIdsSelecionados(idsSelecionados.length === elegiveis.length ? [] : elegiveis);
    };

    const valorTotalSelecionado = useMemo(() => {
        return faturas.filter(f => idsSelecionados.includes(f.id)).reduce((acc, curr) => acc + (curr.valorLiquido || 0), 0);
    }, [idsSelecionados, faturas]);

    const prepararUploadLote = () => {
        if (idsSelecionados.length === 0) return;
        setFormUpload({ numero: '', valor: valorTotalSelecionado.toFixed(2), arquivo: null });
        setShowModalUpload(true);
    };

    const enviarNotaEmLote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (idsSelecionados.length === 0 || !formUpload.arquivo) return;
        try {
            setLoading(true);
            const valorTotalNota = parseFloat(formUpload.valor.toString().replace('R$', '').replace(/\./g, '').replace(',', '.'));
            const promises = idsSelecionados.map(idFatura =>
                comissaoService.uploadNotaFiscal(idFatura, formUpload.numero, valorTotalNota, formUpload.arquivo!)
            );
            await Promise.all(promises);
            setAlert({ message: 'Nota Fiscal enviada com sucesso!', type: 'success' });
            setShowModalUpload(false);
            setIdsSelecionados([]);
            carregarDados();
        } catch (err) {
            setLoading(false);
            setAlert({ message: 'Erro ao enviar nota.', type: 'error' });
        }
    };

    const aceitarNegociacao = async (idNegociacao: number) => {
        if (!window.confirm('Ao aceitar, você concorda com os termos e as parcelas serão geradas no seu financeiro. Continuar?')) return;
        setLoading(true);
        try {
            await comissaoService.aceitarNegociacaoParceiro(idNegociacao);
            setAlert({ message: 'Negociação aceita com sucesso!', type: 'success' });
            carregarDados();
        } catch (error) {
            setAlert({ message: 'Erro ao aceitar negociação.', type: 'error' });
            setLoading(false);
        }
    };

    const formatMoney = (val: number) => val?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
    const formatDate = (dateStr: string) => dateStr ? format(new Date(dateStr), 'dd/MM/yyyy') : '-';

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'PAGO': case 'QUITADO': return <span className="badge bg-success">Pago</span>;
            case 'EM_ANDAMENTO': case 'APROVADA': return <span className="badge bg-primary">Aprovada</span>;
            case 'PENDENTE': case 'EM_ANALISE': return <span className="badge bg-warning text-dark">Em Análise</span>;
            case 'ATRASADO': case 'REPROVADA': return <span className="badge bg-danger">Reprovada</span>;
            default: return <span className="badge bg-secondary">{status}</span>;
        }
    };

    return (
        <>
            <style>{`
                .animate-pulse-red { animation: pulse-red 1.5s infinite; }
                @keyframes pulse-red {
                  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
                  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
                  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                }
                .fade-in { animation: fadeIn 0.5s ease-in; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>

            {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
            {loading && <Spinner loading={loading} />}

            {/* --- BANNER DE ALERTA CRÍTICO --- */}
            {faturasAtrasadas.length > 0 && (
                <div className="alert alert-danger border-0 rounded-0 m-0 py-3 shadow sticky-top" style={{ zIndex: 1020 }}>
                    <div className="container-fluid px-md-5 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="fs-3 me-3 animate-pulse-red" />
                            <div>
                                <h6 className="mb-0 fw-bold">AÇÃO NECESSÁRIA: Notas Fiscais em Atraso</h6>
                                <small>Você possui {faturasAtrasadas.length} fatura(s) pendentes de nota há mais de 5 dias.</small>
                            </div>
                        </div>
                        <button className="btn btn-dark fw-bold px-4 shadow-sm" onClick={() => { setActiveTab('FATURAS'); setIdsSelecionados(faturasAtrasadas.map(f => f.id)); }}>
                            RESOLVER AGORA
                        </button>
                    </div>
                </div>
            )}

            <div className="container-fluid mt-4 px-md-5">
                
                {/* --- HEADER COM SELEÇÃO DE PROMOTORA --- */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom">
                    <div>
                        <h3 className="fw-bold mb-0 text-dark">Portal Financeiro</h3>
                        <div className="mt-3">
                            <label className="text-muted small fw-bold mb-1">SELECIONE A PROMOTORA:</label>
                            <div className="input-group" style={{ maxWidth: '450px' }}>
                                <span className="input-group-text bg-white border-end-0">
                                    <FontAwesomeIcon icon={faBuilding} className="text-secondary" />
                                </span>
                                
                                {/* SELECT CORRIGIDO */}
                                <select 
                                    className="form-select border-start-0 fw-bold shadow-sm"
                                    value={idParceiroSelecionado}
                                    onChange={(e) => setIdParceiroSelecionado(Number(e.target.value))}
                                >
                                    <option value={0}>Selecione...</option>
                                    {promotoras.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.nome} {p.cnpj ? ` - ${p.cnpj}` : ''}
                                        </option>
                                    ))}
                                </select>

                            </div>
                        </div>
                    </div>

                    {extrato && (
                        <div className="card border-0 bg-light shadow-sm px-4 py-2 text-center mt-3 mt-md-0">
                            <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Saldo em Conta</small>
                            <div className="fw-bold text-primary fs-4">{formatMoney(extrato.saldoAtual)}</div>
                        </div>
                    )}
                </div>

                {/* NAVEGAÇÃO */}
                <ul className="nav nav-pills mb-4">
                    <li className="nav-item">
                        <button className={`nav-link px-4 position-relative ${activeTab === 'FATURAS' ? 'active bg-dark' : 'text-muted'}`} onClick={() => setActiveTab('FATURAS')}>
                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" /> Faturas
                            {faturasAtrasadas.length > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-light">
                                    {faturasAtrasadas.length}
                                </span>
                            )}
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link px-4 position-relative ${activeTab === 'NEGOCIACOES' ? 'active bg-dark' : 'text-muted'}`} onClick={() => setActiveTab('NEGOCIACOES')}>
                            <FontAwesomeIcon icon={faHandshake} className="me-2" /> Acordos
                            {negociacoes.filter(n => n.status === 'PENDENTE').length > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark border border-light">
                                    {negociacoes.filter(n => n.status === 'PENDENTE').length}
                                </span>
                            )}
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link px-4 ${activeTab === 'EXTRATO' ? 'active bg-dark' : 'text-muted'}`} onClick={() => setActiveTab('EXTRATO')}>
                            <FontAwesomeIcon icon={faWallet} className="me-2" /> Extrato
                        </button>
                    </li>
                </ul>

                {/* --- CONTEÚDO DAS ABAS --- */}
                
                {/* ABA FATURAS */}
                {activeTab === 'FATURAS' && (
                    <div className="fade-in">
                        <div className={`card border-0 shadow-sm mb-3 ${idsSelecionados.length > 0 ? 'bg-primary text-white' : ''}`}>
                            <div className="card-body d-flex justify-content-between align-items-center py-2">
                                <div className="small fw-bold">
                                    {idsSelecionados.length > 0
                                        ? `${idsSelecionados.length} faturas selecionadas | Total: ${formatMoney(valorTotalSelecionado)}`
                                        : 'Selecione as faturas para realizar o envio da Nota Fiscal'}
                                </div>
                                {idsSelecionados.length > 0 && (
                                    <button className="btn btn-light btn-sm px-4 fw-bold shadow" onClick={prepararUploadLote}>
                                        <FontAwesomeIcon icon={faFileUpload} className="me-2" /> ANEXAR NOTA
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light text-secondary small text-uppercase">
                                        <tr>
                                            <th className="text-center py-3" style={{ width: '50px' }}>
                                                <input type="checkbox" className="form-check-input"
                                                    onChange={toggleSelecionarTudo}
                                                    checked={faturas.length > 0 && idsSelecionados.length === faturas.filter(isElegivelSelecao).length}
                                                />
                                            </th>
                                            <th className="py-3">Fatura / Nota</th>
                                            <th className="py-3 text-center">Data Emissão</th>
                                            <th className="py-3 text-end">Valor Líquido</th>
                                            <th className="py-3 text-center">Status</th>
                                            <th className="py-3 text-center">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {faturas.length > 0 ? faturas.map(fatura => {
                                            const disponivel = isElegivelSelecao(fatura);
                                            const atrasada = isPrazoCritico(fatura.dataCriacao) && !fatura.idNotaFiscal && fatura.status !== 'PAGA';

                                            return (
                                                <tr key={fatura.id} className={`${atrasada ? 'table-danger' : ''} ${idsSelecionados.includes(fatura.id) ? 'table-active' : ''}`}>
                                                    <td className="text-center">
                                                        {disponivel && (
                                                            <input type="checkbox" className="form-check-input"
                                                                checked={idsSelecionados.includes(fatura.id)}
                                                                onChange={() => toggleSelecao(fatura.id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className={`fw-bold ${atrasada ? 'text-danger' : 'text-dark'}`}>
                                                            Fatura #{fatura.id}
                                                            {atrasada && <FontAwesomeIcon icon={faCircleExclamation} className="ms-2 animate-pulse-red" />}
                                                        </div>
                                                        <small className="text-muted">
                                                            {fatura.numeroNotaFiscal ? `NF-e: ${fatura.numeroNotaFiscal}` : 'Nota Fiscal pendente'}
                                                        </small>
                                                    </td>
                                                    <td className="text-center text-muted small">{formatDate(fatura.dataCriacao)}</td>
                                                    <td className="text-end fw-bold">{formatMoney(fatura.valorLiquido)}</td>
                                                    <td className="text-center">{getStatusBadge(fatura.statusNotaFiscal || fatura.status)}</td>
                                                    <td className="text-center">
                                                        {atrasada && (
                                                            <button className="btn btn-danger btn-sm fw-bold px-3 shadow-sm" onClick={() => { setIdsSelecionados([fatura.id]); setTimeout(prepararUploadLote, 50); }}>
                                                                Regularizar
                                                            </button>
                                                        )}
                                                        {fatura.statusNotaFiscal === 'REPROVADA' && (
                                                            <div className="text-danger small fw-bold" title={fatura.motivoReprovacao} style={{ cursor: 'help' }}>
                                                                <FontAwesomeIcon icon={faExclamationTriangle} /> Corrigir Nota
                                                            </div>
                                                        )}
                                                        {!atrasada && disponivel && idsSelecionados.length === 0 && (
                                                            <button className="btn btn-sm btn-outline-dark px-3" onClick={() => { toggleSelecao(fatura.id); setTimeout(prepararUploadLote, 50); }}>Enviar</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr><td colSpan={6} className="text-center py-5 text-muted">
                                                {idParceiroSelecionado === 0 ? "Selecione uma promotora acima." : "Nenhuma fatura encontrada."}
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- ABA NEGOCIAÇÕES --- */}
                {activeTab === 'NEGOCIACOES' && (
                    <div className="fade-in">
                        {negociacoes.length === 0 ? (
                            <div className="text-center py-5 card border-0 shadow-sm">
                                <FontAwesomeIcon icon={faHandshake} size="3x" className="text-muted mb-3" />
                                <h5>Nenhum acordo pendente</h5>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {negociacoes.map(n => (
                                    <div className="col-12" key={n.id}>
                                        <div className={`card border-0 shadow-sm ${n.status === 'PENDENTE' ? 'border-start border-4 border-warning' : ''}`}>
                                            <div className="card-body d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="fw-bold mb-1">{n.nomeNegociacao}</h6>
                                                    <small className="text-muted">{n.qtdParcelas} parcelas • Total {formatMoney(n.valorTotal)}</small>
                                                </div>
                                                <div>
                                                    {n.status === 'PENDENTE' ? (
                                                        <button className="btn btn-dark btn-sm px-4 fw-bold" onClick={() => aceitarNegociacao(n.id)}>ACEITAR ACORDO</button>
                                                    ) : getStatusBadge(n.status)}
                                                    <button className="btn btn-light btn-sm ms-2" onClick={() => setNegociacaoExpandida(negociacaoExpandida === n.id ? null : n.id)}>
                                                        <FontAwesomeIcon icon={negociacaoExpandida === n.id ? faChevronUp : faChevronDown} />
                                                    </button>
                                                </div>
                                            </div>
                                            {negociacaoExpandida === n.id && (
                                                <div className="card-footer bg-white border-top">
                                                    <table className="table table-sm table-borderless small mb-0">
                                                        <thead><tr className="text-muted"><th>Parcela</th><th>Vencimento</th><th className="text-end">Valor</th></tr></thead>
                                                        <tbody>
                                                            {n.parcelas.map(p => (
                                                                <tr key={p.id}><td>{p.numero}</td><td>{formatDate(p.dataVencimento)}</td><td className="text-end fw-bold">{formatMoney(p.valor)}</td></tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* --- ABA EXTRATO --- */}
                {activeTab === 'EXTRATO' && extrato && (
                    <div className="fade-in">
                        <div className="row mb-4">
                            <div className="col-md-6 mb-3 mb-md-0">
                                <div className="card border-0 shadow-sm p-4 h-100">
                                    <h6 className="text-muted small fw-bold text-uppercase">Movimentação do Período</h6>
                                    <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                                        <span className="text-success fw-bold"><FontAwesomeIcon icon={faArrowUp} className="me-2" /> Créditos</span>
                                        <span className="fs-5 fw-bold text-success">{formatMoney(extrato.totalCreditosPeriodo)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-danger fw-bold"><FontAwesomeIcon icon={faArrowDown} className="me-2" /> Débitos</span>
                                        <span className="fs-5 fw-bold text-danger">{formatMoney(extrato.totalDebitosPeriodo)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card border-0 shadow-sm p-4 h-100 bg-dark text-white">
                                    <h6 className="text-white-50 small fw-bold text-uppercase">Saldo Disponível</h6>
                                    <h2 className="fw-bold mt-2 mb-0">{formatMoney(extrato.saldoAtual)}</h2>
                                    <small className="text-white-50">Valores liberados para saque/pagamento.</small>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white fw-bold py-3">Histórico de Lançamentos</div>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light small">
                                        <tr>
                                            <th className="ps-4">Data</th>
                                            <th>Descrição</th>
                                            <th className="text-center">Tipo</th>
                                            <th className="text-end pe-4">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {extrato.lancamentos.map(lanc => (
                                            <tr key={lanc.id}>
                                                <td className="ps-4 text-muted small">{formatDate(lanc.dataMovimento)}</td>
                                                <td className="fw-medium text-dark">{lanc.descricao}</td>
                                                <td className="text-center">
                                                    <span className={`badge ${lanc.tipoLancamento === 'CREDITO' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                                        {lanc.tipoLancamento}
                                                    </span>
                                                </td>
                                                <td className={`text-end pe-4 fw-bold ${lanc.tipoLancamento === 'CREDITO' ? 'text-success' : 'text-danger'}`}>
                                                    {lanc.tipoLancamento === 'DEBITO' ? '- ' : '+ '} {formatMoney(lanc.valorLancamento)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showModalUpload && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-dark text-white">
                                <h5 className="modal-title fw-bold">Anexar Nota Fiscal</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalUpload(false)}></button>
                            </div>
                            <form onSubmit={enviarNotaEmLote}>
                                <div className="modal-body p-4">
                                    <div className="alert alert-info border-0 shadow-sm mb-4">
                                        <small className="d-block text-uppercase fw-bold opacity-75">Resumo do Envio</small>
                                        <div className="fs-5 fw-bold">{idsSelecionados.length} Fatura(s) Selecionada(s)</div>
                                        <div className="fs-6">Valor Total da Nota: <strong>{formatMoney(parseFloat(formUpload.valor))}</strong></div>
                                    </div>
                                    <div className="form-floating mb-3">
                                        <input type="text" className="form-control" id="txtNumero" required value={formUpload.numero} onChange={e => setFormUpload({ ...formUpload, numero: e.target.value })} placeholder="Número" />
                                        <label htmlFor="txtNumero">Número da Nota Fiscal</label>
                                    </div>
                                    <div className="form-floating mb-3">
                                        <input type="text" className="form-control" id="txtValor" required value={formUpload.valor} onChange={e => setFormUpload({ ...formUpload, valor: e.target.value })} placeholder="Valor" />
                                        <label htmlFor="txtValor">Confirmar Valor (R$)</label>
                                    </div>
                                    <div className="mb-2">
                                        <label className="form-label small fw-bold text-muted">Arquivo da Nota (PDF ou Imagem)</label>
                                        <input type="file" className="form-control" accept="application/pdf, image/*" required onChange={e => setFormUpload({ ...formUpload, arquivo: e.target.files ? e.target.files[0] : null })} />
                                    </div>
                                </div>
                                <div className="modal-footer bg-light border-0">
                                    <button type="button" className="btn btn-link text-muted text-decoration-none fw-bold" onClick={() => setShowModalUpload(false)}>CANCELAR</button>
                                    <button type="submit" className="btn btn-dark px-5 fw-bold" disabled={loading}>{loading ? 'PROCESSANDO...' : 'CONFIRMAR E ENVIAR'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default FinanceiroParceiroComponent;